import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserInfo } from '../../types/common/common.ts';
import { assertSessionPortal, readStoredAuth, type StoredSession } from '../../services/auth/persistence.ts';
import { normalizeAccessPermissions } from '@qcdoc/mock-runtime/contracts/accessModel.ts';

export interface AuthState {
  user: UserInfo | null;
  accessToken: string | null;
  expiresAt: string | null;
  isInitialized: boolean;
  roles: string[];
  isLoading: boolean;
}

function normalizeUserPermissions(user: UserInfo | null): UserInfo | null {
  if (!user?.accessProfile) return user;
  return { ...user, accessProfile: { ...user.accessProfile, permissions: normalizeAccessPermissions(user.accessProfile.permissions) } };
}

const authSlice = createSlice({
  name: 'common/auth',
  initialState: () => {
    const state = readStoredAuth();
    return { ...state, user: normalizeUserPermissions(state.user) };
  },
  reducers: {
    setUser: {
      prepare(user: UserInfo | null) {
        assertSessionPortal(user);
        return { payload: normalizeUserPermissions(user) };
      },
      reducer(state, action: PayloadAction<UserInfo | null>) {
        state.user = action.payload;
        state.roles = action.payload?.groups ?? [];
      },
    },
    setSession: {
      prepare(session: StoredSession) {
        assertSessionPortal(session.user);
        return { payload: { ...session, user: normalizeUserPermissions(session.user)! } };
      },
      reducer(state, { payload }: PayloadAction<StoredSession>) {
        state.user = payload.user;
        state.accessToken = payload.token;
        state.expiresAt = payload.expiresAt;
        state.roles = payload.user.groups ?? [];
        state.isInitialized = true;
      },
    },
    setInitialized(state, action: PayloadAction<boolean>) { state.isInitialized = action.payload; },
    setLoading(state, action: PayloadAction<boolean>) { state.isLoading = action.payload; },
    clear() {
      return { user: null, accessToken: null, expiresAt: null, roles: [], isInitialized: false, isLoading: false };
    },
    patch(state, action: PayloadAction<Partial<AuthState>>) {
      Object.assign(state, action.payload);
      if (action.payload.user !== undefined) state.user = normalizeUserPermissions(action.payload.user);
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;